import { useState } from 'react';
import { useApp } from '../../state/AppContext';
import Icon from '../../lib/Icon';
import { card, cardPad } from '../../lib/ui';

const inputStyle = {
  width: '100%', padding: '10px 12px', border: '1px solid var(--color-border-strong)',
  borderRadius: 'var(--radius-sm)', background: 'var(--color-white)', fontSize: 14,
} as const;
const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6, color: 'var(--color-fg-2)' } as const;

// The RFQ this bid responds to (matches the pending invitation on Bid Submission).
const RFQ = {
  id: 'RFQ-2026-004', title: 'Fleet Vehicle Insurance — Class 2 & 3',
  method: 'e-Bidding (sealed)', deadline: '2026-06-28', estimate: '$7.2M',
  buyer: 'Maria B. · Procurement', category: 'Vehicle Insurance',
  lpp: '$7,200,000', invited: '4 insurers', coverage: 'Full Comprehensive',
};

export default function BidForm() {
  const { go, L } = useApp();

  const RFQ_ROWS: [string, string, boolean][] = [
    [L('RFQ No', 'เลขที่ RFQ'), RFQ.id, true],
    [L('Title', 'ชื่อเรื่อง'), RFQ.title, false],
    [L('Buyer', 'ผู้ซื้อ'), RFQ.buyer, false],
    [L('Method', 'วิธีการ'), RFQ.method, false],
    [L('Category', 'หมวดหมู่'), RFQ.category, false],
    [L('Est. Value', 'มูลค่าประมาณการ'), RFQ.estimate, true],
    [L('LPP Reference', 'อ้างอิง LPP'), RFQ.lpp, true],
    [L('Invited', 'ผู้ได้รับเชิญ'), RFQ.invited, false],
    [L('Coverage', 'ความคุ้มครอง'), RFQ.coverage, false],
  ];

  const [amount, setAmount] = useState('');
  const [validity, setValidity] = useState('90');
  const [delivery, setDelivery] = useState('');
  const [notes, setNotes] = useState('');
  const [consent, setConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const canSubmit = amount.trim().length > 0 && consent;

  if (submitted) {
    return (
      <div style={{ ...cardPad(40), maxWidth: 560, margin: '0 auto', textAlign: 'center' }}>
        <div style={{ width: 56, height: 56, borderRadius: 999, background: 'var(--color-success-bg)', color: 'var(--color-success)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: 26 }}><Icon name="check" /></div>
        <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 10 }}>{L('Bid Submitted & Sealed', 'ยื่นและปิดผนึกข้อเสนอแล้ว')}</div>
        <div style={{ fontSize: 13, color: 'var(--color-fg-3)', marginBottom: 6 }}>{L('Your bid reference is', 'รหัสอ้างอิงข้อเสนอของคุณคือ')}</div>
        <div style={{ display: 'inline-block', fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 16, padding: '6px 14px', borderRadius: 'var(--radius-sm)', background: 'var(--color-ink-50)', marginBottom: 18 }}>BID-2026-014</div>
        <div style={{ fontSize: 13, color: 'var(--color-fg-3)', maxWidth: 420, margin: '0 auto 22px' }}>
          {L('Your identity and price stay masked from evaluators until the deadline', 'ตัวตนและราคาของคุณจะถูกปกปิดจากผู้ประเมินจนถึงกำหนดส่ง')} ({RFQ.deadline}). {L('You can revise the bid any time before it closes.', 'คุณสามารถแก้ไขข้อเสนอได้ตลอดเวลาก่อนปิดรับ')}
        </div>
        <button onClick={() => go('bidsubmit')} className="hv-primary" style={{ padding: '10px 20px', background: 'var(--color-primary)', color: '#fff', borderRadius: 'var(--radius-md)', fontWeight: 600, fontSize: 14 }}>{L('Back to My Bids', 'กลับไปหน้าข้อเสนอของฉัน')}</button>
      </div>
    );
  }

  return (
    <div>
      <button onClick={() => go('bidsubmit')} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: 'var(--color-fg-2)', marginBottom: 16 }}><Icon name="arrow-left" />{L('Back to Bid Submission', 'กลับไปหน้ายื่นข้อเสนอ')}</button>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'start' }}>
        {/* Left: RFQ information */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={cardPad(20)}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 12 }}>
              <div style={{ fontWeight: 700 }}>{L('RFQ Details', 'รายละเอียด RFQ')}</div>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-error)', whiteSpace: 'nowrap' }}>{L('Deadline', 'กำหนดส่ง')} {RFQ.deadline}</span>
            </div>
            {RFQ_ROWS.map(([k, v, mono]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '8px 0', borderBottom: '1px solid var(--color-border)', fontSize: 13 }}>
                <span style={{ color: 'var(--color-fg-3)', flex: 'none' }}>{k}</span>
                <span style={{ fontWeight: 600, textAlign: 'right', fontFamily: mono ? 'var(--font-mono)' : 'var(--font-sans)' }}>{v}</span>
              </div>
            ))}
          </div>

          {/* Sealed-bid notice */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '14px 18px', background: 'var(--color-blue-100)', border: '1px solid var(--color-blue-300)', borderRadius: 'var(--radius-md)' }}>
            <Icon name="eye-off" style={{ color: 'var(--color-blue-700)', flex: 'none', marginTop: 2 }} />
            <div style={{ fontSize: 12, color: 'var(--color-blue-700)' }}>{L('Blind bidding — your company name and price are masked from evaluators until the deadline. Bids are scored by the AI on price, quality, delivery, performance, and compliance.', 'การเสนอราคาแบบปิด — ชื่อบริษัทและราคาของคุณจะถูกปกปิดจากผู้ประเมินจนถึงกำหนดส่ง ข้อเสนอจะถูกให้คะแนนโดย AI ตามราคา คุณภาพ การส่งมอบ ผลงาน และการปฏิบัติตามข้อกำหนด')}</div>
          </div>
        </div>

        {/* Right: bid form */}
        <div style={{ ...card, padding: 24 }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 18 }}>{L('Your Bid', 'ข้อเสนอของคุณ')}</div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={labelStyle}>{L('Bid Amount (USD, incl. VAT)', 'จำนวนเงินเสนอ (ดอลลาร์, รวม VAT)')}</label>
            <input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="numeric" placeholder={L('e.g. 6,950,000', 'เช่น 6,950,000')} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>{L('Price Validity (days)', 'อายุราคา (วัน)')}</label>
            <input value={validity} onChange={(e) => setValidity(e.target.value)} inputMode="numeric" style={inputStyle} />
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>{L('Delivery / Coverage Start', 'เริ่มการส่งมอบ / ความคุ้มครอง')}</label>
          <input value={delivery} onChange={(e) => setDelivery(e.target.value)} type="date" style={inputStyle} />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>{L('Technical Notes (optional)', 'หมายเหตุทางเทคนิค (ไม่บังคับ)')}</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={L('Scope inclusions, assumptions, or clarifications…', 'ขอบเขตที่รวม สมมติฐาน หรือคำชี้แจง…')} style={{ ...inputStyle, minHeight: 96, resize: 'vertical' }} />
        </div>

        <div style={{ marginBottom: 18 }}>
          <label style={labelStyle}>{L('Supporting Documents (optional)', 'เอกสารประกอบ (ไม่บังคับ)')}</label>
          <div style={{ border: '1.5px dashed var(--color-border-strong)', borderRadius: 'var(--radius-sm)', padding: 20, textAlign: 'center', color: 'var(--color-fg-3)', fontSize: 13 }}>
            <Icon name="upload" style={{ fontSize: 20 }} />
            <div style={{ marginTop: 6 }}>{L('Drop files here or click to upload (PDF, XLSX)', 'วางไฟล์ที่นี่หรือคลิกเพื่ออัปโหลด (PDF, XLSX)')}</div>
          </div>
        </div>

        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 18, cursor: 'pointer' }}>
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} style={{ marginTop: 2 }} />
          <span style={{ fontSize: 13, color: 'var(--color-fg-2)' }}>{L('I confirm this bid is genuine, independently prepared, and free of any collusion with other bidders.', 'ข้าพเจ้ายืนยันว่าข้อเสนอนี้เป็นของจริง จัดทำขึ้นอย่างอิสระ และปราศจากการสมรู้ร่วมคิดกับผู้เสนอราคารายอื่น')}</span>
        </label>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button onClick={() => go('bidsubmit')} className="hv-ink" style={{ padding: '10px 18px', border: '1px solid var(--color-border-strong)', borderRadius: 'var(--radius-md)', fontWeight: 600, fontSize: 14, background: 'var(--color-white)', color: 'var(--color-fg-1)' }}>{L('Cancel', 'ยกเลิก')}</button>
          <button
            onClick={() => canSubmit && setSubmitted(true)}
            disabled={!canSubmit}
            className={canSubmit ? 'hv-primary' : undefined}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: canSubmit ? 'var(--color-primary)' : 'var(--color-ink-100)', color: '#fff', borderRadius: 'var(--radius-md)', fontWeight: 600, fontSize: 14, cursor: canSubmit ? 'pointer' : 'not-allowed' }}
          >
            <Icon name="lock" />{L('Submit Sealed Bid', 'ยื่นข้อเสนอแบบปิดผนึก')}
          </button>
        </div>
        </div>
      </div>
    </div>
  );
}
